import { FC, memo, useCallback, useMemo, useState } from 'react';

import { Dropdown, MenuProps, message } from 'antd';
import { AddToQueueIcon, AddedToLibrary, AddToLibrary, AddToPlaylist } from '../Icons';

// Services
import { albumsService } from '../../services/albums';
import { playlistService } from '../../services/playlists';

// Utils
import { useTranslation } from 'react-i18next';
import { resolveTrackRecordingId } from '../../utils/resolveRecordingId';

// Interface
import type { Album } from '../../interfaces/albums';

// Redux
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fetchMyPlaylists, yourLibraryActions } from '../../store/slices/yourLibrary';
import { uiActions } from '../../store/slices/ui';
import { albumActions } from '../../store/slices/album';
import { useCascadeSelections } from '../../hooks/useCascadeSelections';
import { CascadeMenu } from './CascadeMenu';
import { usePlayback } from '../../contexts/PlaybackContext';

interface AlbumActionsWrapperProps {
  album: Album;
  isCurrent?: boolean;
  trigger?: ('contextMenu' | 'click')[];
  children: React.ReactNode | React.ReactNode[];
}

export const AlbumActionsWrapper: FC<AlbumActionsWrapperProps> = memo((props) => {
  const { children, album } = props;

  const { t } = useTranslation(['playlist']);

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user?.id);
  const myAlbums = useAppSelector((state) => state.yourLibrary.myAlbums);
  const currentAlbumFollowing = useAppSelector((state) =>
    state.album.album?.id === album.id ? state.album.following : null
  );
  const myPlaylists = useAppSelector((state) => state.yourLibrary.myPlaylists);
  const albumTracks = useAppSelector((state) => state.album.tracks);
  const albumTrackIds = useAppSelector((state) =>
    state.album.tracks
      .map((track) => {
        if (typeof track?.id === 'string' && track.id.trim().length > 0) {
          return track.id.trim();
        }
        const uri = typeof track?.uri === 'string' ? track.uri.trim() : '';
        if (uri.startsWith('spotify:track:')) {
          const [, , trackId] = uri.split(':');
          if (trackId && trackId.trim().length > 0) {
            return trackId.trim();
          }
        }
        return '';
      })
      .filter((id): id is string => id.length > 0)
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [isApplyingCascade, setIsApplyingCascade] = useState(false);
  const [isQueueAppendPending, setIsQueueAppendPending] = useState(false);

  const inLibrary = useMemo(() => {
    if (currentAlbumFollowing !== null) {
      return currentAlbumFollowing;
    }
    return myAlbums.some((p) => p.id === album.id);
  }, [album.id, currentAlbumFollowing, myAlbums]);

  const mode = inLibrary ? 'remove' : 'save';
  const {
    scopes: cascadeScopes,
    selections: cascadeSelections,
    setSelection: setCascadeSelection,
    resetSelections: resetCascadeSelections,
  } = useCascadeSelections({
    entityType: 'album',
    mode,
  });

  const handleUserValidation = useCallback(
    (button?: boolean) => {
      if (!user) {
        dispatch(button ? uiActions.openLoginButton() : uiActions.openLoginTooltip());
        return false;
      }
      return true;
    },
    [dispatch, user]
  );

  const { queueSession, queueStatus, performQueueAction } = usePlayback();

  const options = useMemo(() => {
    const items: any[] = myPlaylists.map((p) => {
      return {
        key: p.id,
        label: p.name,
        onClick: async () => {
          if (!handleUserValidation()) return;
          const {
            data: { items: tracks },
          } = await albumsService.fetchAlbumTracks(album.id);
          const uris = tracks.map((t) => t.uri);
          playlistService.addPlaylistItems(p!.id, uris, p?.snapshot_id!).then(() => {
            message.open({
              type: 'success',
              content: t('Added to playlist'),
            });
          });
        },
      };
    });

    if (myPlaylists.length) {
      items.unshift({ type: 'divider' });
    }

    items.unshift({
      label: t('New playlist'),
      key: 'new',
      onClick: async () => {
        if (!handleUserValidation()) return;
        const {
          data: { items: tracks },
        } = await albumsService.fetchAlbumTracks(album.id);
        const uris = tracks.map((t) => t.uri);
        return playlistService.createPlaylist(user!, { name: album.name }).then((response) => {
          const playlist = response.data;
          playlistService.addPlaylistItems(playlist.id, uris, playlist.snapshot_id!).then(() => {
            dispatch(fetchMyPlaylists());
            message.success(t('Added to playlist'));
          });
        });
      },
    });

    return items;
  }, [myPlaylists, t, handleUserValidation, album.id, album.name, user, dispatch]);

  const cascadeLabels = useMemo(() => {
    if (mode === 'save') {
      return {
        album: 'Save album',
        recordings: 'Save recordings',
        tracks: 'Save tracks',
      };
    }
    return {
      album: 'Remove album',
      recordings: 'Remove recordings',
      tracks: 'Remove tracks',
    };
  }, [mode]);

  const handleCascadeToggle = useCallback(
    (scope: (typeof cascadeScopes)[number], next: boolean) => {
      setCascadeSelection(scope, next);
    },
    [setCascadeSelection]
  );

  const handleCascadeApply = useCallback(async () => {
    if (!handleUserValidation(true)) return;
    setIsApplyingCascade(true);
    try {
      const cascades = {
        recordings: cascadeSelections.recordings ?? true,
        tracks: cascadeSelections.tracks ?? true,
      };

      if (mode === 'save') {
        await dispatch(
          yourLibraryActions.saveAlbumWithCascade({
            albumId: album.id,
            albumName: album.name,
            cascades,
            trackIds: albumTrackIds.length ? albumTrackIds : undefined,
          })
        ).unwrap();
        dispatch(albumActions.setFollowing({ following: true }));
      } else {
        await dispatch(
          yourLibraryActions.removeAlbumWithCascade({
            albumId: album.id,
            albumName: album.name,
            cascades,
            trackIds: albumTrackIds.length ? albumTrackIds : undefined,
          })
        ).unwrap();
        dispatch(albumActions.setFollowing({ following: false }));
      }
      setMenuOpen(false);
      resetCascadeSelections();
    } catch (error) {
      // Notifications handled within thunks.
    } finally {
      setIsApplyingCascade(false);
    }
  }, [
    album.id,
    album.name,
    albumTrackIds,
    cascadeSelections.recordings,
    cascadeSelections.tracks,
    dispatch,
    handleUserValidation,
    mode,
    resetCascadeSelections,
  ]);

  const albumQueueSegments = useMemo(() => {
    type Segment =
      | { type: 'recording'; recordingId: string }
      | {
          type: 'tracks';
          trackUris: string[];
          trackDurationsMs: Array<number | null>;
          trackMetadata: Array<Record<string, unknown>>;
        };

    const segments: Segment[] = [];
    let currentRecordingId: string | null = null;
    let trackBuffer: string[] = [];
    let durationBuffer: Array<number | null> = [];
    let metadataBuffer: Array<Record<string, unknown>> = [];

    const flushSegment = () => {
      if (currentRecordingId) {
        segments.push({ type: 'recording', recordingId: currentRecordingId });
      } else if (trackBuffer.length) {
        segments.push({
          type: 'tracks',
          trackUris: trackBuffer,
          trackDurationsMs: durationBuffer,
          trackMetadata: metadataBuffer
        });
      }
      currentRecordingId = null;
      trackBuffer = [];
      durationBuffer = [];
      metadataBuffer = [];
    };

    albumTracks.forEach((track) => {
      const recordingId = resolveTrackRecordingId(track) ?? null;
      const trackUri =
        typeof track.uri === 'string' && track.uri.trim().length
          ? track.uri.trim()
          : typeof track.id === 'string' && track.id.trim().length
          ? track.id.trim().startsWith('spotify:track:')
            ? track.id.trim()
            : `spotify:track:${track.id.trim()}`
          : null;
      const durationMs =
        typeof track.duration_ms === 'number' && Number.isFinite(track.duration_ms)
          ? Math.trunc(track.duration_ms)
          : null;
      const albumImageUrl =
        Array.isArray(album?.images) && album.images.length ? album.images[0]?.url : undefined;

      if (recordingId && recordingId !== currentRecordingId) {
        flushSegment();
        currentRecordingId = recordingId;
        return;
      }

      if (!recordingId && currentRecordingId) {
        flushSegment();
      }

      if (recordingId) {
        currentRecordingId = recordingId;
      } else if (trackUri) {
        trackBuffer.push(trackUri);
        durationBuffer.push(durationMs);
        const artists =
          Array.isArray(track.artists) && track.artists.length
            ? track.artists
                .map((artist) => (artist && typeof artist.name === 'string' ? artist.name.trim() : ''))
                .filter((name) => name.length > 0)
            : [];
        metadataBuffer.push({
          title: typeof track.name === 'string' ? track.name.trim() : undefined,
          artists: artists.length ? artists : undefined,
          albumName: album?.name ?? undefined,
          albumImageUrl,
          durationMs: durationMs ?? undefined
        });
      }
    });

    flushSegment();
    return segments;
  }, [album, albumTracks]);

  const handleQueueAppend = useCallback(async () => {
    if (isQueueAppendPending) return;
    if (!handleUserValidation(true)) return;
    if (!queueSession || queueStatus !== 'ready') {
      void message.info('Start playback to build a queue before adding items.');
      return;
    }
    if (!albumQueueSegments.length) {
      void message.warning('No tracks available to add right now.');
      return;
    }

    setIsQueueAppendPending(true);
    try {
      for (const segment of albumQueueSegments) {
        if (segment.type === 'recording') {
          await performQueueAction('append-recordings', { recordingIds: [segment.recordingId] });
        } else if (segment.trackUris.length) {
          await performQueueAction('append-tracks', {
            trackUris: segment.trackUris,
            trackDurationsMs: segment.trackDurationsMs,
            trackMetadata: segment.trackMetadata
          });
        }
      }
      void message.success('Added to queue');
    } catch (error) {
      console.error('Failed to append album to queue', error);
      void message.error('Failed to add to queue');
    } finally {
      setIsQueueAppendPending(false);
    }
  }, [
    albumQueueSegments,
    handleUserValidation,
    isQueueAppendPending,
    performQueueAction,
    queueSession,
    queueStatus,
  ]);

  const items = useMemo(() => {
    const items: MenuProps['items'] = [];

    items.push({
      label: t(inLibrary ? 'Remove from Your Library' : 'Add to Your Library'),
      key: 'cascade-album',
      icon: inLibrary ? (
        <AddedToLibrary style={{ height: 16, width: 16, marginInlineEnd: 0 }} />
      ) : (
        <AddToLibrary style={{ height: 16, width: 16, marginInlineEnd: 0 }} />
      ),
      className: 'cascade-menu__trigger-item',
      popupClassName: 'cascade-menu__popup',
      children: [
        {
          key: 'cascade-album-options',
          label: (
            <CascadeMenu
              entityType='album'
              mode={mode}
              scopes={cascadeScopes}
              selections={cascadeSelections}
              labels={cascadeLabels}
              onToggle={handleCascadeToggle}
              applyLabel='Apply'
              onApply={handleCascadeApply}
              onCancel={() => {
                resetCascadeSelections();
                setMenuOpen(false);
              }}
              applyDisabled={isApplyingCascade}
            />
          ),
          style: { padding: 0, height: 'auto' },
        },
      ],
    });

    items.push(
      {
        label: t('Add to queue'),
        key: '3',
        disabled: isQueueAppendPending,
        icon: <AddToQueueIcon />,
        onClick: () => {
          void handleQueueAppend();
        },
      },
      { type: 'divider' },
      {
        label: t('Add to playlist'),
        icon: <AddToPlaylist />,
        key: '1',
        children: options,
      }
    );

    return items;
  }, [
    cascadeLabels,
    cascadeScopes,
    cascadeSelections,
    handleQueueAppend,
    handleCascadeApply,
    handleCascadeToggle,
    inLibrary,
    isQueueAppendPending,
    isApplyingCascade,
    mode,
    options,
    resetCascadeSelections,
    t,
  ]);

  return (
    <Dropdown
      menu={{ items, triggerSubMenuAction: 'hover' }}
      trigger={props.trigger}
      open={menuOpen}
      onOpenChange={(next) => {
        setMenuOpen(next);
        if (!next) {
          resetCascadeSelections();
          setIsApplyingCascade(false);
        }
      }}
    >
      {children}
    </Dropdown>
  );
});
